// Bileli · modules/spaces/lib — localStorage persistence for the drain. The JOURNAL is the safety core
// (doc §4 "journal before ack"): once a forum exists on Telegram, its coordinates live here until the
// backend confirms the ack — so a crashed/failed ack is retried from the journal and the forum is NEVER
// created twice (a duplicate forum on the account is unrecoverable garbage).

import type { SpaceDirectoryEntry, SpaceJournalEntry } from '../model/drain-logic';
// Bileli 2026-07-29: SHOWROOM DISABLED (product decision). The showroom journal + directory helpers below
// are commented out, not deleted — the cohort journal/directory + shared worker/bot helpers are untouched.
// import type { ShowroomJournalEntry } from '../model/showroom-logic';

const JOURNAL_KEY = 'bileli:spaces-drain-journal';
const WORKER_KEY = 'bileli:spaces-worker-id';
const DIRECTORY_KEY = 'bileli:spaces-directory';
// const SHOWROOM_JOURNAL_KEY = 'bileli:showroom-drain-journal';
// Last bot username seen on any claimed job — lets the ensure sweeps re-promote the bot on EXISTING
// forums without a job in hand (env-driven value; FALLBACK_BOT covers a never-drained session).
const BOT_USERNAME_KEY = 'bileli:spaces-bot-username';

function lsRead<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : undefined;
  } catch {
    return undefined;
  }
}

function lsWrite(key: string, value: unknown): void {
  try {
    if (value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable — the drain still works, it just loses crash-resume safety
  }
}

export function readJournal(): Record<string, SpaceJournalEntry> {
  return lsRead<Record<string, SpaceJournalEntry>>(JOURNAL_KEY) ?? {};
}

export function writeJournalEntry(entry: SpaceJournalEntry): void {
  lsWrite(JOURNAL_KEY, { ...readJournal(), [entry.jobId]: entry });
}

export function clearJournalEntry(jobId: string): void {
  const { [jobId]: removed, ...rest } = readJournal();
  void removed;
  lsWrite(JOURNAL_KEY, rest);
}

// Bileli 2026-07-29: SHOWROOM journal + directory disabled (product decision). Commented out, not deleted.
// export function readShowroomJournal(): Record<string, ShowroomJournalEntry> {
//   return lsRead<Record<string, ShowroomJournalEntry>>(SHOWROOM_JOURNAL_KEY) ?? {};
// }
// export function writeShowroomJournalEntry(entry: ShowroomJournalEntry): void {
//   lsWrite(SHOWROOM_JOURNAL_KEY, { ...readShowroomJournal(), [entry.jobId]: entry });
// }
// export function clearShowroomJournalEntry(jobId: string): void {
//   const { [jobId]: removed, ...rest } = readShowroomJournal();
//   void removed;
//   lsWrite(SHOWROOM_JOURNAL_KEY, rest);
// }
// const SHOWROOM_DIRECTORY_KEY = 'bileli:showroom-directory';
// export interface ShowroomDirectoryEntry {
//   orgId: string;
//   username: string;
//   /** The forum's chat id in the fork's native '-100…' STRING form — feed to openCourseSpace as-is. */
//   peerId: string;
//   createdAt: number;
// }
// export function readShowroomDirectory(): Record<string, ShowroomDirectoryEntry> {
//   return lsRead<Record<string, ShowroomDirectoryEntry>>(SHOWROOM_DIRECTORY_KEY) ?? {};
// }
// export function recordShowroomDirectory(entry: ShowroomDirectoryEntry): void {
//   lsWrite(SHOWROOM_DIRECTORY_KEY, { ...readShowroomDirectory(), [entry.orgId]: entry });
// }

export function rememberBotUsername(username: string | null | undefined): void {
  if (username) lsWrite(BOT_USERNAME_KEY, username);
}

export function getKnownBotUsername(): string | undefined {
  return lsRead<string>(BOT_USERNAME_KEY);
}

/** Stable per-browser-profile worker identity (doc §2.1) — minted once, reused forever. */
export function getWorkerId(): string {
  const existing = lsRead<string>(WORKER_KEY);
  if (existing) return existing;
  const minted = `web-drain-${crypto.randomUUID()}`;
  lsWrite(WORKER_KEY, minted);
  return minted;
}

// The DIRECTORY (unlike the journal) is permanent: it's the teacher-facing «Созданные пространства» list,
// keyed by cohortId so a re-provisioned cohort overwrites its own row. Local-only until the backend serves
// the fields on CohortDto (docs/backend/TASK-cohort-space-fields.md).
export function readDirectory(): Record<string, SpaceDirectoryEntry> {
  return lsRead<Record<string, SpaceDirectoryEntry>>(DIRECTORY_KEY) ?? {};
}

export function writeDirectoryEntry(entry: SpaceDirectoryEntry): void {
  lsWrite(DIRECTORY_KEY, { ...readDirectory(), [entry.cohortId]: entry });
}
